"use client";

import * as React from "react";

import { resolveAppBaseUrl, type AppConfig } from "../config/get-config";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

type HomeJourneyAnalyticsProps = {
  runtimeConfig: AppConfig | null;
};

function getLinkLabel(link: HTMLAnchorElement) {
  return (
    link.getAttribute("aria-label") ||
    link.textContent?.replace(/\s+/g, " ").trim() ||
    "unknown"
  );
}

function getJourneyTarget(pathname: string) {
  if (pathname.startsWith("/signup")) return "signup";
  if (pathname.startsWith("/login")) return "login";
  if (pathname.startsWith("/premium")) return "premium";
  if (pathname.startsWith("/mock-test") || pathname.startsWith("/mock-tests")) {
    return "mock_test";
  }
  if (pathname.startsWith("/practice")) return "practice";
  if (pathname === "/" || pathname === "") return "app_home";
  return pathname.replace(/^\/+/, "").replace(/\/+/g, "_") || "app";
}

function getPageSection(link: HTMLAnchorElement) {
  const section = link.closest("section[id], header, footer");
  if (!section) return "unknown";

  if (section instanceof HTMLElement && section.id) {
    return section.id;
  }

  return section.tagName.toLowerCase();
}

export default function HomeJourneyAnalytics({
  runtimeConfig,
}: HomeJourneyAnalyticsProps) {
  React.useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;

    const appOrigin = (() => {
      try {
        return new URL(resolveAppBaseUrl(runtimeConfig)).origin;
      } catch {
        return "";
      }
    })();

    if (!appOrigin) return;

    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const link = target.closest("a[href]");
      if (!(link instanceof HTMLAnchorElement)) return;

      let url: URL;
      try {
        url = new URL(link.href, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== appOrigin) return;

      const journeyTarget = getJourneyTarget(url.pathname);
      const section = getPageSection(link);
      const label = getLinkLabel(link);

      url.searchParams.set("ptu_source", "home");
      url.searchParams.set("ptu_entry", journeyTarget);
      url.searchParams.set("ptu_home_path", window.location.pathname || "/");
      url.searchParams.set("ptu_home_section", section);
      link.href = url.toString();

      window.gtag?.("event", "home_to_app_click", {
        event_category: "journey",
        event_label: label,
        journey_target: journeyTarget,
        home_section: section,
        home_path: window.location.pathname || "/",
        outbound_path: url.pathname,
        transport_type: "beacon",
      });
    };

    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, [runtimeConfig]);

  return null;
}
